import { Box, Typography } from '@mui/material';

import { popoverFilterMenuStyles as styles } from './popoverFilterMenu.styles';
import { AppIcon, appStrings, FilterCategoryConfig } from '../..';

const BUTTON_STRINGS = appStrings.buttonText;

type DoubleLayerFilterMenuProps = Readonly<{
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  categories: FilterCategoryConfig[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
}>;

export const DoubleLayerFilterMenu = ({
  setAnchorEl,
  categories,
  selectedCategory,
  setSelectedCategory,
}: DoubleLayerFilterMenuProps) => {
  /**
   * first layer - the parent categories
   */
  const FirstLayer = () => {
    return (
      <Box sx={styles.categoriesContainerSx}>
        {categories.map((category) => (
          <Box
            key={category.label}
            id={`filter-category-${category.label}`}
            sx={styles.categoryItemSx}
            onClick={() => setSelectedCategory(category.items[0]?.category || null)}
          >
            <Typography variant="text-sm-semibold" sx={styles.categoryLabelSx}>
              {category.label}
            </Typography>

            <Box sx={styles.categoryIndicatorSx}>
              {category.items.some((item) => item.isSelected) && <Box sx={styles.activeDotSx} />}

              <AppIcon
                iconName="ChevronRight"
                diameter="16px"
                color="Colors/Foreground/fg-quinary"
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  /**
   * second layer - children items within a parent category
   */
  const SecondLayer = () => {
    const itemsInSelectedCategory = categories.find(
      (cat) => cat.items[0]?.category === selectedCategory,
    )?.items;

    return (
      <Box sx={styles.itemsContainerSx}>
        {/* BACK BUTTON */}
        <Box
          id="filter-back-button"
          sx={styles.backButtonSx}
          onClick={() => setSelectedCategory(null)}
        >
          <AppIcon iconName="ChevronLeft" diameter="16px" color="Colors/Foreground/fg-quinary" />
          <Typography variant="text-sm-semibold" sx={styles.backLabelSx}>
            {BUTTON_STRINGS.back}
          </Typography>
        </Box>

        {/* FILTER ITEMS */}
        {itemsInSelectedCategory?.map((item) => (
          <Box
            key={item.value}
            id={`filter-subcategory-${item.value}`}
            sx={styles.filterItemSx}
            onClick={() => {
              item.onClick();
              if (item.closeOnClick) {
                setAnchorEl(null);
                setSelectedCategory(null);
              }
            }}
          >
            {item.icon && (
              <AppIcon
                iconName={item.icon}
                diameter="16px"
                color="Colors/Foreground/fg-quinary"
                strokeWidth="bold"
              />
            )}

            <Typography variant="text-sm-semibold" sx={styles.itemLabelSx}>
              {item.label}
            </Typography>

            {item.rightIcon && (
              <AppIcon
                iconName={item.rightIcon}
                diameter="16px"
                color="Colors/Foreground/fg-quinary"
              />
            )}

            {/* IF NO RIGHT ICON WE WILL SHOW A CHECKMARK */}
            {!item.rightIcon && (
              <Box sx={styles.checkboxSx}>
                <AppIcon
                  iconName="Check"
                  diameter="16px"
                  color={
                    // keep it transparent instead of hiding when it's not selected to ensure the parent doesn't grow
                    item.isSelected ? 'Colors/Foreground/fg-quinary' : 'Colors/Base/Transparent'
                  }
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {/* LIST OF PARENT CATEGORIES WILL SHOW IF PARENT CATEGORY IS NOT SELECTED */}
      {selectedCategory === null ? (
        <FirstLayer />
      ) : (
        // LIST OF CHILDREN ITEMS WILL SHOW WHEN PARENT CATEGORY IS SELECTED
        <SecondLayer />
      )}
    </>
  );
};
